import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../assets/datalumio webp/Lumio.webp";

const navItems = [
  { label: "Home", path: "/" },
  {
    label: "Features",
    path: "/features",
    dropdown: [
      { label: "AI Agent", path: "/ai-agent" },
      { label: "Qualitative Analysis", path: "/features/qualitative-data-analysis" },
      { label: "Quantitative Analysis", path: "/features/quantitative-data-analysis" },
      { label: "Data Cleaning", path: "/features/data-cleaning" },
      { label: "Data Dashboard", path: "/features/data-visualization-dashboard" },
      { label: "PDF Analysis", path: "/features/pdf-analysis" },
      { label: "Data Integration", path: "/features/data-integration" }
    ]
  },
  {
    label: "Solutions",
    path: "/solutions",
    dropdown: [
      { label: "Survey Data Analysis", path: "/solutions/survey-data-analysis" },
      { label: "Thesis & Dissertation Data Analysis", path: "/solutions/thesis-dissertation-data-analysis" },
      { label: "Excel Data Analysis", path: "/solutions/excel-data-analysis" },
      { label: "Research Data Analysis", path: "/solutions/research-data-analysis" },
      { label: "Customer Feedback Analysis", path: "/solutions/customer-feedback-analysis" },
      { label: "Interview Transcript Analysis", path: "/solutions/interview-transcript-analysis" },
      { label: "Statistical Analysis", path: "/solutions/statistical-analysis" }
    ]
  },
  { label: "Pricing", path: "/pricing" },
  { label: "Blog", path: "/blogs" },
  // { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Contact Us", path: "/contact-us" },
  { label: "Demo", path: "/demo" },
  { label: "Use Cases", path: "/templates" },
  { label: "About Us", path: "/about" },
  { label: "FAQ", path: "/faq" },
  { label: "API", path: "/api-docs" },
  // { label: "Integrations", path: "/integrations" },
];

const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Label of the dropdown expanded in the mobile drawer, or null. Keyed by
  // label rather than a boolean so each dropdown opens independently.
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setDrawerOpen(false);
        setOpenDropdown(null);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavClick = (item) => {
    if (item.external) {
      window.open(item.path, "_blank");
    } else {
      navigate(item.path);
      setDrawerOpen(false);
    }
  };

  const isActive = (item) => !item.external && location.pathname === item.path;

  return (
    <>
      <style>{`
        .dl-navbar-wrapper { width: 100%; background: transparent; padding: 0; box-sizing: border-box; }
        .dl-navbar { max-width: 100%; width: 100%; margin: 0; background-color: #F5F7FF; display: flex; flex-direction: column; }
        .dl-toolbar { display: flex; align-items: center; justify-content: space-between; height: 84px; padding: 0 28px; gap: 12px; }
        .dl-brand { display: flex; align-items: center; gap: 8px; cursor: pointer; flex-shrink: 0; }
        .dl-brand-logo { width: 152px; height: 152px; object-fit: contain; display: block; }
        .dl-nav-links { display: flex; align-items: center; gap: 10px; flex: 1; justify-content: center; flex-wrap: nowrap; }
        .dl-nav-link { text-decoration: none; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: 15px; color: #475569; cursor: pointer; padding: 10px 12px; border-radius: 7px; transition: all 0.25s ease; white-space: nowrap; background: none; border: none; outline: none; line-height: 1; }
        .dl-nav-link:hover { background: #DBE7FF; color: #1E293B; }
        .dl-nav-link.active { font-weight: 600; background: #DBE7FF; color: #1E293B; }
        .dl-auth { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .dl-btn-login { text-decoration: none; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; background: #fcfdff; border: 1px solid #0F172A; color: #1E293B; border-radius: 7px; padding: 6px 16px; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: 13px; cursor: pointer; transition: background 0.2s ease; white-space: nowrap; }
        .dl-btn-login:hover { background: #DBE7FF; }
        .dl-btn-signup { text-decoration: none; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; background: #2563EB; border: none; color: #fff; border-radius: 7px; padding: 6px 16px; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: 13px; cursor: pointer; transition: background 0.25s ease; white-space: nowrap; }
        .dl-btn-signup:hover { background: #1D4ED8; }
        .dl-hamburger { display: none; background: none; border: none; cursor: pointer; padding: 4px; flex-shrink: 0; }
        .dl-drawer { display: flex; flex-direction: column; background: #F5F7FF; padding: 0 20px; border-top: 0px solid #E2E8F0; gap: 2px; border-radius: 0 0 8px 8px; max-height: 0; overflow: hidden; transition: max-height 0.4s ease-out, padding 0.4s ease, border-top 0.4s ease; }
        .dl-drawer.open { max-height: calc(100vh - 84px); padding: 8px 20px 18px; border-top: 1px solid #E2E8F0; overflow-y: auto; }
        .dl-drawer .dl-nav-link { text-align: left; padding: 12px 14px; width: 100%; font-size: 14px; border-radius: 8px; justify-content: flex-start; }
        .dl-drawer-auth { display: flex; gap: 12px; margin-top: 15px; }

        .dl-nav-dropdown-wrapper { position: relative; display: inline-flex; align-items: center; }
        .dl-nav-dropdown { position: absolute; top: 100%; left: 0; background: #fff; min-width: 220px; box-shadow: none; border-radius: 8px; padding: 8px 0; display: none; flex-direction: column; z-index: 9999; border: 1px solid #E2E8F0; }
        .dl-nav-dropdown-wrapper:hover .dl-nav-dropdown { display: flex; }
        .dl-nav-dropdown-item { padding: 10px 16px; text-decoration: none; color: #475569; font-family: 'Poppins', sans-serif; font-size: 14px; transition: background 0.2s, color 0.2s; white-space: nowrap; }
        .dl-nav-dropdown-item:hover { background: #DBE7FF; color: #1E293B; font-weight: 500; }
        .dl-chevron { font-size: 10px; margin-left: 6px; transition: transform 0.2s; display: inline-block; }
        .dl-nav-dropdown-wrapper:hover .dl-chevron { transform: rotate(180deg); }

        .dl-drawer-dropdown-wrapper { width: 100%; }
        .dl-drawer-dropdown { display: flex; flex-direction: column; padding-left: 14px; border-left: 2px solid #E2E8F0; margin-left: 14px; margin-top: 4px; }
        .dl-drawer-dropdown-item { padding: 10px 14px; text-decoration: none; color: #475569; font-family: 'Poppins', sans-serif; font-size: 13px; border-radius: 8px; transition: background 0.2s; }
        .dl-drawer-dropdown-item:hover { background: #DBE7FF; color: #1E293B; }
        
        @media (max-width: 1100px) { 
          .dl-nav-links, .dl-auth { display: none; } 
          .dl-hamburger { display: flex; } 
          .dl-toolbar { padding: 0 20px; }
        }
        @media (min-width: 1101px) {
          .dl-drawer { display: none !important; }
        }
        @media (max-width: 480px) { 
          .dl-navbar-wrapper { padding: 0 10px; } 
          .dl-toolbar { padding: 0 12px; height: 74px; } 
          .dl-brand-logo { width: 110px; height: 110px; }
          .dl-drawer { padding: 0 15px; }
          .dl-drawer.open { padding: 8px 15px 18px; max-height: calc(100vh - 74px); overflow-y: auto; }
          .dl-drawer-auth { flex-wrap: wrap; gap: 8px; }
          .dl-drawer-auth a { flex: 1; min-width: 100px; text-align: center; }
        }
      `}</style>

      <div className="dl-navbar-wrapper">
        <nav className="dl-navbar">
          <div className="dl-toolbar">
            <Link to="/" className="dl-brand">
              <img src={logo} alt="DataLumio logo" className="dl-brand-logo" width={152} height={152} />
            </Link>
            <div className="dl-nav-links">
              {navItems.map((item) => {
                if (item.dropdown) {
                  return (
                    <div className="dl-nav-dropdown-wrapper" key={item.label}>
                      {item.path ? (
                        <Link
                          to={item.path}
                          className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                        >
                          {item.label} <span className="dl-chevron">▼</span>
                        </Link>
                      ) : (
                        // Category with no page of its own: the label only opens the menu.
                        <button
                          type="button"
                          className={`dl-nav-link${item.dropdown.some((s) => s.path === location.pathname) ? " active" : ""}`}
                          aria-haspopup="true"
                        >
                          {item.label} <span className="dl-chevron">▼</span>
                        </button>
                      )}
                      <div className="dl-nav-dropdown">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.path}
                            className="dl-nav-dropdown-item"
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return item.external ? (
                  <a
                    key={item.label}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
            <div className="dl-auth">
              {isMounted && isLoggedIn ? (
                <Link to="/welcome" className="dl-btn-signup">View Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="dl-btn-login">Login</Link>
                  <Link to="/signup" className="dl-btn-signup">Sign up</Link>
                </>
              )}
            </div>
            <button
              className="dl-hamburger"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
            >
              {drawerOpen ? <CloseIcon /> : <HamburgerIcon />}
            </button>
          </div>
          <div className={`dl-drawer${drawerOpen ? " open" : ""}`}>
            {navItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.label} className="dl-drawer-dropdown-wrapper">
                    <div
                      className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                      style={{ width: "100%", justifyContent: "space-between" }}
                      onClick={() => setOpenDropdown((cur) => (cur === item.label ? null : item.label))}
                    >
                      {item.path ? (
                        <Link
                          to={item.path}
                          style={{ color: "inherit", textDecoration: "none", flex: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrawerOpen(false);
                          }}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <span style={{ flex: 1 }}>{item.label}</span>
                      )}
                      <span className="dl-chevron" style={{ transform: openDropdown === item.label ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", padding: "0 10px" }}>▼</span>
                    </div>
                    {openDropdown === item.label && (
                      <div className="dl-drawer-dropdown">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.path}
                            className="dl-drawer-dropdown-item"
                            onClick={() => setDrawerOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return item.external ? (
                <a
                  key={item.label}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`dl-nav-link${isActive(item) ? " active" : ""}`}
                  onClick={() => setDrawerOpen(false)}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="dl-drawer-auth">
              {isMounted && isLoggedIn ? (
                <Link to="/welcome" className="dl-btn-signup" style={{ margin: '0', flex: 'none', width: 'fit-content', padding: '8px 24px' }} onClick={() => setDrawerOpen(false)}>View Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="dl-btn-login" onClick={() => setDrawerOpen(false)}>Login</Link>
                  <Link to="/signup" className="dl-btn-signup" onClick={() => setDrawerOpen(false)}>Sign up</Link>
                </>
              )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}